import React, { useState } from 'react';
import { UitkButtonSize, UitkPrimaryButton } from 'uitk-react-button';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkTextInput } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkText } from 'uitk-react-text';
import { Transition, TransitionStatus } from 'react-transition-group';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { useBexApi } from '@shared-ui/bex-api-context';
import useNavigationParams from 'hooks/useNavigationParams';
import { useFeedbackQuery } from '../../graphql/__generated__/types';

export const getFeedbackType = (feedback: string | boolean) =>
  feedback.toString() === 'true' ? 'positive' : 'negative';
const TRANSITION_DURATION = 300;
const defaultTransitionStyle: React.CSSProperties = {
  maxHeight: 0,
  transition: [
    `opacity ${TRANSITION_DURATION}ms ease-in 0s`,
    `background ${TRANSITION_DURATION}ms ease-in 0s`,
    `max-height ${TRANSITION_DURATION}ms ease-in 0s`,
  ].join(', '),
};

interface FormData {
  wasHelpful: boolean;
  suggestions: string;
}

const ContentFeedback = () => {
  const { context } = useBexApi();
  const { hubId } = useNavigationParams();
  const { data, loading, error } = useFeedbackQuery({ variables: { hubId, context } });
  const initialFormData: FormData = {
    wasHelpful: null,
    suggestions: '',
  };
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [step, setStep] = useState(0);
  const analytics = useAnalytics();
  const handleTextFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      suggestions: e.currentTarget.value,
    });
  };

  if (loading || error) return null;

  const { feedback } = data.documentation;
  const handleFeedbackSubmission = () => {
    setStep(2);
    const type = getFeedbackType(formData.wasHelpful);
    analytics.trackFeedbackReason({
      type,
      reason: formData.suggestions,
    });
    setFormData(initialFormData);
  };

  const handleFeedbackButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setStep(1);
    const type = getFeedbackType(event.currentTarget.value);
    analytics.trackFeedbackIntent({ type });
    setFormData({
      ...formData,
      wasHelpful: event.currentTarget.value === 'true',
    });
  };

  const transitionStyle: { [state in TransitionStatus]?: React.CSSProperties } = {
    entering: { maxHeight: 0, opacity: 0 },
    entered: { maxHeight: 'initial', opacity: 1 },
    exiting: { maxHeight: 'initial', opacity: 0 },
    exited: { maxHeight: 0, opacity: 0 },
  };

  return (
    <>
      <Transition in={step === 0} timeout={TRANSITION_DURATION}>
        {(state) => (
          <UitkLayoutFlex
            direction="column"
            space="three"
            style={{
              ...defaultTransitionStyle,
              ...transitionStyle[state],
            }}
          >
            <UitkLayoutFlexItem>
              <UitkText size={200} weight="medium">
                {feedback.question.text}
              </UitkText>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkLayoutFlex space="three">
                <UitkPrimaryButton
                  size={UitkButtonSize.SMALL}
                  type="button"
                  onClick={handleFeedbackButtonClick}
                  value="true"
                >
                  <UitkIcon name={feedback.thumpsUp.icon.token} size={UitkIconSize[feedback.thumpsUp.icon.size]} />
                  {feedback.thumpsUp.primary}
                </UitkPrimaryButton>
                <UitkPrimaryButton
                  size={UitkButtonSize.SMALL}
                  type="button"
                  onClick={handleFeedbackButtonClick}
                  value="false"
                >
                  <UitkIcon name={feedback.thumpsDown.icon.token} size={UitkIconSize[feedback.thumpsDown.icon.size]} />
                  {feedback.thumpsDown.primary}
                </UitkPrimaryButton>
              </UitkLayoutFlex>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </Transition>
      <Transition in={step === 1} timeout={TRANSITION_DURATION}>
        {(state) => (
          <UitkLayoutFlex
            direction="column"
            space="four"
            style={{
              ...defaultTransitionStyle,
              ...transitionStyle[state],
            }}
          >
            <UitkLayoutFlexItem>
              <UitkText size={200} weight="medium">
                {feedback.suggestionForm.input.label}
              </UitkText>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
              <UitkTextInput
                label={feedback.suggestionForm.input.placeholder}
                value={formData.suggestions}
                onChange={handleTextFeedbackChange}
                id="suggestions"
              />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem alignSelf="end">
              <UitkPrimaryButton disabled={!formData.suggestions} type="submit" onClick={handleFeedbackSubmission}>
                {feedback.suggestionForm.submit.primary}
              </UitkPrimaryButton>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </Transition>
      <Transition in={step === 2} timeout={TRANSITION_DURATION}>
        {(state) => (
          <UitkLayoutFlex
            direction="column"
            space="four"
            style={{
              ...defaultTransitionStyle,
              ...transitionStyle[state],
            }}
          >
            <UitkLayoutFlexItem>
              <UitkText size={200} weight="medium">
                {feedback.suggestionForm.thankYouMessage.text}
              </UitkText>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </Transition>
    </>
  );
};

export default ContentFeedback;
